import { redirectToLogin } from "@/app/auth-bridge";
import { useAuthStore, type AuthUser } from "@/lib/auth-store";

const DEFAULT_BASE_URL = "/api/v1";

const baseUrl = import.meta.env.VITE_API_BASE_URL || DEFAULT_BASE_URL;

export interface ApiErrorBody {
  code?: string;
  message?: string;
  details?: Array<{ field: string; issue: string }>;
  traceId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody | null;

  constructor(status: number, body: ApiErrorBody | null, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export interface ApiRequestOptions<TBody = unknown> {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: TBody;
  headers?: HeadersInit;
  /**
   * Skip both the Authorization header and the refresh-on-401 retry
   * path. Used by the login endpoint and by the refresh endpoint
   * itself (the latter to avoid infinite recursion).
   */
  skipAuth?: boolean;
  signal?: AbortSignal;
}

// The backend's /auth/refresh returns the same LoginResponse shape as
// /auth/login: a fresh access token PLUS the user summary. Carrying the
// user lets a cold-load restore rebuild the whole session in one call.
interface RefreshResponse {
  accessToken: string;
  user?: AuthUser | null;
}

// Module-level single-flight promise. Concurrent 401s coalesce onto the
// same refresh request. Resolves true on success, false on failure;
// always nulled out when settled so the next 401 can start a fresh
// refresh round.
let refreshPromise: Promise<boolean> | null = null;

export async function apiRequest<TResponse, TBody = unknown>(
  path: string,
  options: ApiRequestOptions<TBody> = {},
): Promise<TResponse> {
  // First attempt.
  const firstResponse = await doFetch(path, options);

  // Happy path or explicit skip — no refresh logic needed.
  if (firstResponse.status !== 401 || options.skipAuth) {
    return finalize<TResponse>(firstResponse);
  }

  // 401 from a request that wanted auth. Try to refresh, then retry once.
  const refreshed = await ensureRefresh();
  if (!refreshed) {
    // Refresh failed (and clearAuth + redirect already happened in
    // ensureRefresh's finally). Surface the original 401 to callers.
    throw await toApiError(firstResponse);
  }

  const retryResponse = await doFetch(path, options);

  // If the retry ALSO 401s, give up rather than loop. The auth store
  // is still populated at this point — we only clear when refresh
  // itself fails. A second 401 indicates either an authorization
  // problem (forbidden disguised as 401) or a server bug; either way
  // we want the caller to see the error, not retry forever.
  return finalize<TResponse>(retryResponse);
}

async function doFetch<TBody>(
  path: string,
  options: ApiRequestOptions<TBody>,
): Promise<Response> {
  const { body, skipAuth, headers, method, signal } = options;

  const finalHeaders = new Headers(headers);
  if (!finalHeaders.has("Content-Type") && body !== undefined) {
    finalHeaders.set("Content-Type", "application/json");
  }
  if (!finalHeaders.has("Accept")) {
    finalHeaders.set("Accept", "application/json");
  }

  if (!skipAuth) {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      finalHeaders.set("Authorization", `Bearer ${token}`);
    }
  }

  const init: RequestInit = {
    method: method ?? "GET",
    headers: finalHeaders,
    // Include cookies so the HttpOnly refresh_token rides along with the
    // refresh request (and any other endpoint that may need cookies).
    credentials: "include",
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  if (signal !== undefined) {
    init.signal = signal;
  }

  return fetch(`${baseUrl}${path}`, init);
}

async function finalize<T>(response: Response): Promise<T> {
  if (!response.ok) {
    throw await toApiError(response);
  }
  // Success with no body — 204 No Content, or a 200 from
  // ResponseEntity.ok().build() (reset-password, forgot-password, etc.).
  // response.json() throws on an empty body, so read text first and only
  // parse when there is something to parse.
  const text = await response.text();
  if (text.length === 0) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

async function toApiError(response: Response): Promise<ApiError> {
  const body = await safeParseJson<ApiErrorBody>(response);
  return new ApiError(
    response.status,
    body,
    body?.message ?? `Request failed with status ${response.status}`,
  );
}

async function safeParseJson<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

function ensureRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = doRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

async function doRefresh(): Promise<boolean> {
  try {
    // skipAuth: don't send the (likely-expired) Authorization header,
    // and crucially don't recurse into the refresh path if /auth/refresh
    // itself returns 401.
    const response = await doFetch<undefined>("/auth/refresh", {
      method: "POST",
      skipAuth: true,
    });
    if (!response.ok) {
      onRefreshFailure();
      return false;
    }
    const data = (await response.json()) as RefreshResponse;
    if (typeof data.accessToken !== "string" || data.accessToken.length === 0) {
      onRefreshFailure();
      return false;
    }
    if (data.user != null) {
      // Cold-load restore: rebuild token + identity together. In-session
      // refreshes also land here, harmlessly re-setting the same user.
      useAuthStore.getState().setAuth(data.accessToken, data.user);
    } else {
      useAuthStore.getState().setAccessToken(data.accessToken);
    }
    return true;
  } catch {
    // Network failure on refresh — treat as auth failure.
    onRefreshFailure();
    return false;
  }
}

function onRefreshFailure(): void {
  useAuthStore.getState().clearAuth();
  redirectToLogin();
}

/**
 * Boot-time session restore. The access token lives only in memory, so a
 * full page load always starts unauthenticated — even when the HttpOnly
 * refresh cookie is still valid. ProtectedRoute awaits this once before
 * deciding to bounce to /login; without it every reload logs the user
 * out. Resolves true when a usable session exists afterwards.
 */
export function restoreSession(): Promise<boolean> {
  if (useAuthStore.getState().accessToken !== null) {
    return Promise.resolve(true);
  }
  return ensureRefresh();
}

// Test-only escape hatch. Keeps the module-level singleton from leaking
// between Vitest test cases. Not exported from any barrel.
export function __resetApiClientForTests(): void {
  refreshPromise = null;
}
